import { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import type { Matcher } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { formatDMY } from '@/lib/format';
import { cn } from '@/lib/utils';

interface DatePickerFieldProps {
  /** Selected day as YYYY-MM-DD, or '' for none. */
  value: string;
  onChange: (iso: string) => void;
  /** Inclusive bounds as YYYY-MM-DD; days outside are disabled. */
  min?: string;
  max?: string;
  /**
   * Extra inclusive YYYY-MM-DD ranges that cannot be picked (e.g. operator-
   * blocked visit dates). Rendered disabled in the calendar.
   */
  disabledRanges?: ReadonlyArray<{ from: string; to: string }>;
  placeholder?: string;
  /** Muted text shown before the date in the trigger (e.g. "Kunjungan:"). */
  prefix?: string;
  /** Extra classes for the trigger button. */
  className?: string;
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
}

/** Parse/format a plain calendar day (no timezone shift — Y/M/D in, Y/M/D out). */
function isoToDate(iso?: string | null): Date | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}
function dateToISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * A single date field: a button showing the day as dd-mm-yyyy, opening a themed
 * calendar. `min`/`max` bound selectable + navigable months. Emits YYYY-MM-DD.
 */
export function DatePickerField({
  value,
  onChange,
  min,
  max,
  disabledRanges,
  placeholder = 'Pilih tanggal',
  prefix,
  className,
  align = 'start',
  disabled,
}: DatePickerFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value);
  const minDate = isoToDate(min);
  const maxDate = isoToDate(max);

  const disabledMatchers: Matcher[] = [];
  if (minDate) disabledMatchers.push({ before: minDate });
  if (maxDate) disabledMatchers.push({ after: maxDate });
  for (const r of disabledRanges ?? []) {
    const from = isoToDate(r.from);
    const to = isoToDate(r.to);
    if (from && to) disabledMatchers.push({ from, to });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn('h-11 gap-2 font-semibold', className)}
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-primary" />
          {prefix && <span className="text-ink-600">{prefix}</span>}
          <span className={cn('tabular-nums', selected ? 'text-ink-900' : 'text-ink-400')}>
            {selected ? formatDMY(value) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? maxDate ?? minDate}
          startMonth={minDate}
          endMonth={maxDate}
          disabled={disabledMatchers.length ? disabledMatchers : undefined}
          onSelect={(day) => {
            if (day) {
              onChange(dateToISO(day));
              setOpen(false);
            }
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
